'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

type ProvisioningStatus = 'idle' | 'pending' | 'success' | 'failed' | 'timeout';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [provisioningStatus, setProvisioningStatus] = useState<ProvisioningStatus>('idle');
  const [provisioningError, setProvisioningError] = useState('');
  const [registeredUser, setRegisteredUser] = useState<{ firstName: string; lastName: string; email: string } | null>(null);
  const { signUp, provisionApimAccess } = useAuth();
  const router = useRouter();

  const runProvisioning = async (firstName: string, lastName: string, userEmail: string) => {
    setProvisioningStatus('pending');
    setProvisioningError('');
    try {
      await provisionApimAccess(firstName, lastName, userEmail);
      setProvisioningStatus('success');
      setTimeout(() => router.push('/portal/dashboard'), 800);
    } catch (apimError: any) {
      console.error('APIM provisioning failed:', apimError);
      const isTimeout = apimError.message?.includes('timed out') || apimError.code === 'TIMEOUT';
      setProvisioningStatus(isTimeout ? 'timeout' : 'failed');
      setProvisioningError(
        apimError.message || 'API provisioning failed. You can retry or continue to the dashboard.'
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Create Supabase user
      await signUp(email, password, { fullName });

      // Step 2: Provision APIM access
      const [firstName, ...lastNameParts] = fullName.split(' ');
      const lastName = lastNameParts.join(' ') || firstName;
      setRegisteredUser({ firstName, lastName, email });

      await runProvisioning(firstName, lastName, email);
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryProvisioning = async () => {
    if (!registeredUser) return;
    await runProvisioning(registeredUser.firstName, registeredUser.lastName, registeredUser.email);
  };

  const handleSkipToDashboard = () => {
    router.push('/portal/dashboard');
  };

  // Show provisioning status overlay after account creation
  if (provisioningStatus !== 'idle' && !loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <Link href="/homepage" className="flex items-center justify-center gap-2 mb-8">
            <Image
              src="/assets/images/ChatGPT_Image_13._feb._2026_10.17.16-1771010403596.png"
              alt="DataConvert Logo"
              width={48}
              height={48}
              className="object-contain"
            />
            <span className="text-2xl font-serif text-foreground">DataConvert</span>
          </Link>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
            {provisioningStatus === 'pending' && (
              <div className="text-center py-6">
                <Icon name="ArrowPathIcon" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <h2 className="text-2xl font-serif text-foreground mb-2">Setting Up API Access</h2>
                <p className="text-muted-foreground">Provisioning your Azure APIM subscription and generating API keys…</p>
                <p className="text-xs text-muted-foreground mt-3">This may take up to 30 seconds</p>
              </div>
            )}

            {provisioningStatus === 'success' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={40} className="text-green-500" />
                </div>
                <h2 className="text-2xl font-serif text-foreground mb-2">Account Ready!</h2>
                <p className="text-muted-foreground">Your API keys have been provisioned. Redirecting to dashboard…</p>
              </div>
            )}

            {(provisioningStatus === 'failed' || provisioningStatus === 'timeout') && (
              <div>
                <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
                  <Icon name="ExclamationTriangleIcon" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-500 mb-1">
                      {provisioningStatus === 'timeout' ? 'Provisioning Timed Out' : 'Provisioning Failed'}
                    </p>
                    <p className="text-xs text-red-400">{provisioningError}</p>
                  </div>
                </div>

                <div className="bg-background/50 border border-border rounded-lg p-4 mb-6">
                  <p className="text-sm font-medium text-foreground mb-1">✅ Account created successfully</p>
                  <p className="text-xs text-muted-foreground">
                    Your account is ready. API key provisioning can be completed from the dashboard.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleRetryProvisioning}
                    className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                  >
                    <Icon name="ArrowPathIcon" size={18} />
                    Retry Provisioning
                  </button>
                  <button
                    onClick={handleSkipToDashboard}
                    className="w-full px-6 py-3 bg-background border border-border text-foreground rounded-lg font-medium hover:bg-muted transition-colors"
                  >
                    Continue to Dashboard
                  </button>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  You can provision API access anytime from the dashboard.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center justify-center gap-2 mb-8">
          <Image src="/assets/images/ChatGPT_Image_13._feb._2026_10.17.16-1771010403596.png" alt="DataConvert Logo" width={48} height={48} className="object-contain" />
          <span className="text-2xl font-serif text-foreground">DataConvert</span>
        </Link>

        {/* Register Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
          <h1 className="text-3xl font-serif text-foreground mb-2">Create Account</h1>
          <p className="text-muted-foreground mb-8">Start converting files today</p>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <Icon name="ExclamationTriangleIcon" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="••••••••"
              />
              <p className="mt-2 text-xs text-muted-foreground">Must be at least 6 characters</p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href="/portal/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link href="/homepage" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to homepage
          </Link>
        </div>
      </div>
    </div>
  );
}